import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { orderBy } from 'lodash';

import { CaseDateRange, ReportingCountsTable } from '../../../components/reporting';
import { LineChart } from '../../../components/charts';
import LoadScreen from '../../../components/load-screen';

import { getCounts } from '../../../actions/countsActions';
import { getHospitals } from '../../../actions/hospitalsActions';

import { prepareCountsDataMonthly, prepareCountsDataDaily, prepareCountsDataQuarterly } from '../../../utils/analytics';

import { presets } from '../../../constants/analytics';
import { countStatuses } from '../../../constants/enums';
import { onPresetChange as setPresetDates } from '../../../utils/date';

import './reporting-page.scss';

const presetOptions = [
  { label: 'This month', value: presets.THIS_MONTH },
  { label: 'This quarter', value: presets.THIS_QUARTER },
  { label: 'This year', value: presets.THIS_YEAR },
  { label: 'Last month', value: presets.LAST_MONTH },
  { label: 'Last quarter', value: presets.LAST_QUARTER },
  { label: 'Last year', value: presets.LAST_YEAR },
];

const filteredFields = ['hospitalName', 'location', 'userName'];
const getInitialFilters = () => {
  const initialFilter = {};

  filteredFields?.forEach((field) => {
    initialFilter[field] = [];
  });

  return initialFilter;
};

const getPresetRange = (value, yearAgo = false) => {
  let minDate;
  let maxDate;

  setPresetDates(value, {
    onMinDateChange: (date) => { minDate = date; },
    onMaxDateChange: (date) => { maxDate = date; },
  }, yearAgo);

  return { minDate, maxDate };
};

const CountReportingPage = () => {
  const dispatch = useDispatch();
  const tenantPrimaryColor = useSelector((state) => state?.tenant.currentTenant.colorPrimary);
  const tenantId = useSelector((state) => state?.tenant.currentTenant.id);
  const hospitals = useSelector((state) => state?.hospitals.list);
  const users = useSelector((state) => state?.users.list);

  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState([]);

  const [preset, setPreset] = useState(presets.THIS_MONTH);
  const [dateRange, setDateRange] = useState(() => getPresetRange(presets.THIS_MONTH));
  const { minDate, maxDate } = dateRange;

  const [presetCompare, setPresetCompare] = useState('');

  const [filter, setFilter] = useState(getInitialFilters());

  const compareRange = useMemo(() => {
    if (!presetCompare) {
      return null;
    }

    const comparePreset = {
      [presets.THIS_MONTH]: presets.LAST_MONTH,
      [presets.THIS_QUARTER]: presets.LAST_QUARTER,
      [presets.THIS_YEAR]: presets.LAST_YEAR,
    }[preset];

    return comparePreset ? getPresetRange(comparePreset, presetCompare === presets.YEAR_AGO) : null;
  }, [preset, presetCompare]);
  const minDateCompare = compareRange?.minDate;
  const maxDateCompare = compareRange?.maxDate;
  const minDateValue = minDate?.valueOf();
  const maxDateValue = maxDate?.valueOf();
  const minDateCompareValue = minDateCompare?.valueOf();
  const maxDateCompareValue = maxDateCompare?.valueOf();

  useEffect(() => {
    if (!tenantId) {
      return;
    }

    dispatch(getHospitals()).catch((err) => console.error(err));
  }, [dispatch, tenantId]);

  useEffect(() => {
    if (!tenantId || !minDate || !maxDate || minDateValue >= maxDateValue) {
      setCounts([]);
      setLoading(false);
      return;
    }

    let active = true;
    const dateRanges = [dateRange];
    if (compareRange) {
      dateRanges.push(compareRange);
    }

    setCounts([]);
    setLoading(true);
    dispatch(getCounts({ dateRanges, withReducer: false }))
      .then((result) => {
        if (active) {
          setCounts(result.filter((count) => count.status === countStatuses.COMPLETED.value));
        }
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => { active = false; };
  }, [dispatch, tenantId, minDateValue, maxDateValue, minDateCompareValue, maxDateCompareValue]);

  const handlePresetChange = (value) => {
    setPreset(value);
    setPresetCompare('');
    setDateRange(getPresetRange(value));
  };

  const handleDateChange = (field, date) => {
    setDateRange((current) => ({
      ...current,
      [field]: date ? moment(date)[field === 'minDate' ? 'startOf' : 'endOf']('day') : null,
    }));
    setPreset(null);
    setPresetCompare('');
  };

  const getChartData = (filteredCounts, min, max) => {
    if (min.month() === max.month() && min.year() === max.year()) {
      return prepareCountsDataDaily(filteredCounts, min.daysInMonth());
    }

    if (min.quarter() === max.quarter() && min.year() === max.year()) {
      return prepareCountsDataQuarterly(filteredCounts, min.quarter());
    }

    return prepareCountsDataMonthly(filteredCounts);
  };

  const handleFilterChange = (field, values) => {
    setFilter({ ...filter, [field]: values });
  };

  const getFilterOptions = () => {
    const hospitalsOptions = [];
    const locationsOptions = [];
    const usersOptions = [];

    counts?.forEach((item) => {
      const { hospitalId, location, completedBy } = item;

      if (hospitalId && !hospitalsOptions?.map((item) => item.id)?.includes(hospitalId)) {
        const hospitalObj = hospitals?.find((item) => item.id === hospitalId);
        if (hospitalObj) {
          hospitalsOptions?.push(hospitalObj);
        }
      }

      if (location && !locationsOptions?.map((item) => item.name).includes(location)) {
        locationsOptions?.push({ id: location, name: location });
      }

      if (completedBy && !usersOptions?.map((item) => item.uid)?.includes(completedBy)) {
        const userObj = users?.find((user) => user.uid === completedBy);
        if (userObj) {
          usersOptions?.push({ ...userObj, id: userObj.uid });
        }
      }
    });

    return { hospitalName: hospitalsOptions, location: locationsOptions, userName: usersOptions };
  };

  const handleRemoveFilterItem = (key, value) => {
    setFilter({ ...filter, [key]: filter[key].filter((item) => item !== value) });
  };

  // Memoized data

  const filteredCounts = useMemo(() => {
    if (!minDate || !maxDate) {
      return [];
    }

    const sortedCounts = counts ? orderBy(counts, 'completedAt', 'desc') : [];
    return (
      sortedCounts.filter((item) => {
        let persistence = true;

        if (filter?.hospitalName?.length && !filter?.hospitalName?.includes(item.hospitalId)) {
          persistence = false;
        }

        if (filter?.location?.length && !filter?.location?.includes(item.location)) {
          persistence = false;
        }

        if (filter?.userName?.length && !filter?.userName?.includes(item.completedBy)) {
          persistence = false;
        }

        return persistence && minDate.isBefore(item.completedAt) && maxDate.isAfter(item.completedAt) && !!item.active;
      })
    );
  }, [counts, minDate, maxDate, filter]);

  const countsChartData = useMemo(() => {
    if (!minDate || !maxDate) {
      return [];
    }

    const data = getChartData(filteredCounts, minDate, maxDate);

    if (compareRange) {
      const comparedCounts = counts?.filter((item) => minDateCompare.isBefore(item.completedAt) && maxDateCompare.isAfter(item.completedAt));
      const compareData = getChartData(comparedCounts, minDateCompare, maxDateCompare);

      if (preset === presets.THIS_QUARTER && presetCompare === presets.PREVIOUS_PERIOD) {
        const previousQuarter = compareData?.map((item) => ({
          data: item.data?.map((i, index) => ({ x: data[0]?.data[index]?.x, y: i.y })),
          id: `${item.id}_compare`
        }));
        return [...previousQuarter, ...data];
      }

      return [...compareData?.map((item) => ({ ...item, id: `${item.id}_compare` })), ...data];
    }

    return data;
  }, [counts, filteredCounts, minDate, maxDate, minDateCompare, maxDateCompare, compareRange, preset, presetCompare]);

  const presetCompareOptions = useMemo(() => {
    const options = [
      { label: 'Don\'t compare', value: '' }
    ];

    if ([presets.THIS_YEAR, presets.THIS_QUARTER, presets.THIS_MONTH].includes(preset)) {
      options?.push({ label: 'Previous period', value: presets.PREVIOUS_PERIOD });
    }

    if ([presets.THIS_QUARTER, presets.THIS_MONTH].includes(preset)) {
      options?.push({ label: 'Year ago', value: presets.YEAR_AGO });
    }

    return options;
  }, [preset]);

  return (
    <div className='page-container reporting-page__container'>
      {loading && <LoadScreen />}
      <div className='page-title'>Counts Report</div>
      <div className='reporting-page__body'>
        <div className='case-reporting-chart__container'>
          <div className='case-reporting-chart'>
            <LineChart
              data={countsChartData}
              colors={countsChartData.length > 1 ? ['var(--app-text-subtle)', tenantPrimaryColor] : tenantPrimaryColor}
              margin={{ top: 20, right: 15, bottom: 30, left: 30 }}
              entity='count'
            />
          </div>
          <CaseDateRange
            minDate={minDate}
            onMinDateChange={(date) => handleDateChange('minDate', date)}
            maxDate={maxDate}
            onMaxDateChange={(date) => handleDateChange('maxDate', date)}
            preset={preset}
            onPresetChange={handlePresetChange}
            presetCompare={presetCompare}
            onPresetChangeCompare={setPresetCompare}
            options={presetOptions}
            compareOptions={presetCompareOptions}
          />
        </div>
        <ReportingCountsTable
          counts={filteredCounts}
          hospitals={hospitals}
          users={users}
          filter={filter}
          filterOptions={getFilterOptions()}
          onFilterChange={handleFilterChange}
          onRemoveFilter={handleRemoveFilterItem}
        />
      </div>
    </div>
  );
};

export default CountReportingPage;
